import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import { Generator } from './components/Generator';
import { Editor } from './components/Editor';
import { ViewMode, GenerationTask } from './types';
import { generateFullHtml } from './lib/htmlTemplate';
import { prepareMarkdownForRender } from './lib/markdown';
import { getDefaultCategory } from './lib/category';
import { 
  onAuthChange, 
  signIn, 
  subscribeTasks,
  saveTask, 
  deleteTask,
  getUserPermission,
  saveUserPermission
} from './services/storageService';
import { localDbService, LocalDraft, TitleOverride } from './services/localDbService';
import { User } from 'firebase/auth';
import { auth } from './firebase';
import { API_BASE, APP_VERSION, BUILD_TIME } from './constants';
import { DocumentSnapshot } from 'firebase/firestore';
import { ChannelManagement } from './components/ChannelManagement';
import { UserPermission } from './types';

// Permission Status Bar Component for verification
const PermissionStatusBar = ({ permission, user, totalCount, appVersion, buildTime }: { permission: UserPermission | null, user: User | null, totalCount: number, appVersion: string, buildTime: string }) => {
  if (!user) return null;
  
  const handleClearCache = async () => {
    if (window.confirm("로컬 캐시를 초기화하시겠습니까? 앱이 새로고침됩니다.")) {
      try {
        await localDbService.clearAll();
        localStorage.removeItem('lastCleanupDate');
        window.location.reload();
      } catch (err) {
        alert("캐시 초기화 중 오류가 발생했습니다.");
      }
    }
  };
  
  return (
    <div className="bg-slate-900 text-xs text-slate-300 px-3 md:px-6 py-2 flex flex-wrap items-center gap-2 md:gap-4 border-b border-slate-700 sticky top-0 z-[100]">
      <div className="flex items-center gap-1">
        <span className="text-slate-500 font-bold uppercase tracking-wider">User:</span>
        <span className="text-white font-bold">{user.email}</span>
      </div>
      <div className="h-3 w-px bg-slate-700"></div>
      <div className="flex items-center gap-1">
        <span className="text-slate-500 font-bold uppercase tracking-wider">Access:</span>
        <span className={permission?.isAdmin ? "text-red-400 font-bold" : "text-green-400 font-bold"}>
          {permission?.isAdmin ? "ADMIN" : "REGULAR USER"}
        </span>
      </div>
      <div className="h-3 w-px bg-slate-700"></div>
      <div className="flex items-center gap-1">
        <span className="text-slate-500 font-bold uppercase tracking-wider">Agents:</span>
        <span className="text-blue-300 font-mono">
          {permission?.allowedAgents.join(', ') || 'NONE'}
        </span>
      </div>
      <div className="h-3 w-px bg-slate-700"></div>
      <div className="flex items-center gap-1">
        <span className="text-slate-500 font-bold uppercase tracking-wider">Docs:</span>
        <span className="text-white font-bold">{totalCount}개</span>
      </div>
      <div className="h-3 w-px bg-slate-700"></div>
      <div className="flex items-center gap-1">
        <span className="text-slate-500 font-bold uppercase tracking-wider">Ver:</span>
        <span className="text-slate-300 font-mono">v{appVersion} · {buildTime}</span>
      </div>
      <div className="flex-1"></div>
      <button 
        onClick={handleClearCache}
        className="bg-slate-700 hover:bg-slate-600 text-white px-2 py-0.5 rounded border border-slate-600 transition-colors flex items-center gap-1"
      >
        <span className="font-bold">로컬 캐시 초기화</span>
      </button>
    </div>
  );
};

function AppContent() {
  const [categories, setCategories] = useState<string[]>(['자동차', '경제', '운동', 'IT', '스포츠', '연예']);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  // Load categories from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('app_categories');
    if (saved) {
      try {
        setCategories(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse categories", e);
      }
    }
  }, []);

  const handleAddCategory = () => {
    if (newCategoryName.trim() && !categories.includes(newCategoryName.trim())) {
      const updated = [...categories, newCategoryName.trim()];
      setCategories(updated);
      localStorage.setItem('app_categories', JSON.stringify(updated));
      setNewCategoryName('');
      setShowAddCategory(false);
    }
  };

  const handleRemoveCategory = (cat: string) => {
    const updated = categories.filter(c => c !== cat);
    setCategories(updated);
    localStorage.setItem('app_categories', JSON.stringify(updated));
  };
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem('app_viewMode');
      if (saved && Object.values(ViewMode).includes(saved as ViewMode)) {
        return saved as ViewMode;
      }
    }
    return ViewMode.GENERATOR;
  });
  const [activeTaskId, setActiveTaskId] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('app_activeTaskId');
    }
    return null;
  });
  const activeTaskIdRef = useRef<string | null>(null);

  // Sync ref with state
  useEffect(() => {
    activeTaskIdRef.current = activeTaskId;
  }, [activeTaskId]);

  // Persist view state to sessionStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('app_viewMode', viewMode);
      if (activeTaskId) {
        sessionStorage.setItem('app_activeTaskId', activeTaskId);
      } else {
        sessionStorage.removeItem('app_activeTaskId');
      }
    }
  }, [viewMode, activeTaskId]);

  const [user, setUser] = useState<User | null>(null);
  const [userPermission, setUserPermission] = useState<UserPermission | null>(null);
  const [lastVisibleDoc, setLastVisibleDoc] = useState<DocumentSnapshot | null>(null);
  const [tasks, setTasks] = useState<GenerationTask[]>([]);
  const tasksRef = useRef<GenerationTask[]>([]);
  
  // Keep ref in sync with state for synchronous access
  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  const visibleTasks = useMemo(() => {
    if (!userPermission) {
      return [];
    }
    if (userPermission.isAdmin || userPermission.allowedAgents.includes('all')) {
      return tasks;
    }
    return tasks.filter(t => userPermission.allowedAgents.includes(t.agentName || ''));
  }, [tasks, userPermission]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });
  const [isSyncing, setIsSyncing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMoreTasks, setHasMoreTasks] = useState(true);
  const [totalTaskCount, setTotalTaskCount] = useState(0);
  const [selectedAgentName, setSelectedAgentName] = useState<string | 'all'>('all');
  const navigate = useNavigate();
  const appStartTimeRef = useRef<number>(Date.now());
  const unsubscribeNewTasksRef = useRef<(() => void) | null>(null);

  // Derive unique agents from tasks for admin management
  const allAvailableAgents = useMemo(() => {
    const uniqueNames = Array.from(new Set(tasks.map(t => t.agentName).filter(Boolean)));
    return uniqueNames.sort();
  }, [tasks]);

  const agents = useMemo(() => {
    if (!userPermission) return [];
    
    if (userPermission.isAdmin || userPermission.allowedAgents.includes('all')) {
      // Admins see all agents that exist in the tasks
      const uniqueNames = Array.from(new Set(tasks.map(t => t.agentName).filter(Boolean)));
      return uniqueNames.sort().map(name => ({ id: name as string, name: name as string }));
    }
    
    // Regular users only see what's in their allowedAgents list
    return userPermission.allowedAgents
      .filter(name => name !== 'all')
      .sort()
      .map(name => ({ id: name, name }));
  }, [tasks, userPermission]);

  // Initialize Local DB
  useEffect(() => {
    const initLocalDb = async () => {
      try {
        await localDbService.init();
      } catch (err) {
        console.error("[App] Local DB init failed:", err);
      }
    };
    initLocalDb();
  }, []);

  const lastSubscribedUidRef = useRef<string | null>(null);

  // Auth listener
  useEffect(() => {
    console.log("[App] Auth listener effect started");
    if (!auth) {
      console.error("[App] auth is not initialized!");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    const unsubscribe = onAuthChange(async (u) => {
      console.log("[App] Auth state changed:", u ? `User: ${u.uid}` : "No user");
      
      const uidChanged = u?.uid !== lastSubscribedUidRef.current;
      setUser(u);
      
      if (!u) {
        lastSubscribedUidRef.current = null;
        setTasks([]);
        setUserPermission(null);
        setIsLoading(false);
        return;
      }

      // Fetch permissions with error handling to prevent app crash
      let permission: UserPermission | null = null;
      try {
        permission = await getUserPermission(u.email || '');
      } catch (e) {
        console.error("[App] Failed to fetch permissions from DB:", e);
      }
      
      // Default admin for laziosh@gmail.com
      if (u.email === 'laziosh@gmail.com') {
        permission = {
          id: u.email,
          email: u.email,
          allowedAgents: ['all'],
          isAdmin: true,
          updatedAt: Date.now()
        };
        // Admins CAN save their own permission
        await saveUserPermission(permission).catch(e => console.error("Admin save failed:", e));
      }
      
      // Fallback for other users if DB fetch failed
      if (!permission && u.email) {
        permission = {
          id: u.email,
          email: u.email,
          allowedAgents: [],
          isAdmin: false,
          updatedAt: Date.now()
        };
      }
      
      setUserPermission(permission);

      // Force selection if only one agent is allowed
      if (permission && !permission.isAdmin && permission.allowedAgents.length === 1 && permission.allowedAgents[0] !== 'all') {
        setSelectedAgentName(permission.allowedAgents[0]);
      }

      if (uidChanged) {
        lastSubscribedUidRef.current = u.uid;
        
        if (unsubscribeNewTasksRef.current) unsubscribeNewTasksRef.current();

        setIsLoading(true);
        setError(null);
        setTasks([]); // Clear tasks before new subscription
        tasksRef.current = [];

        // 1. Start Real-time Listener (Populates initial and future documents)
        // Pass allowedAgents to enforce query-level isolation
        unsubscribeNewTasksRef.current = subscribeTasks(
          async (newTasks) => {
            if (newTasks.length === 0) {
              setTasks([]);
              setTotalTaskCount(0);
              setIsLoading(false);
              return;
            }

            // Reconcile each task with IndexedDB:
            // - Firestore newer → write Firestore data into LocalDB
            // - LocalDB newer   → keep LocalDB-only fields in the in-memory task
            //   (편집 핸들러들이 'DO NOT Trigger Firestore save' 정책으로 인해
            //    updatedAt 등이 LocalDB에만 존재할 수 있음 → snapshot 발화 시 회귀 방지)
            const reconciledTasks = await Promise.all(newTasks.map(async (task) => {
              const [existingDraft, titleOverride] = await Promise.all([
                localDbService.getDraft(task.id),
                localDbService.getTitleOverride(task.id)
              ]);

              const localUpdatedAt = Math.max(
                existingDraft?.updatedAt || 0,
                titleOverride?.updatedAt || 0
              );
              const firestoreNewer = (!existingDraft && !titleOverride)
                || (task.updatedAt && task.updatedAt > localUpdatedAt);

              if (firestoreNewer) {
                await localDbService.saveDraft(task.id, task.outputContent, task.articleTitle || '', task.presetName, {
                  isWeekend: task.isWeekend,
                  category: task.category,
                  isRegistered: task.isRegistered,
                  agentName: task.agentName,
                  createdAt: task.createdAt,
                  updatedAt: task.updatedAt,
                  notionPageId: task.notionPageId
                });
                return mergeLocalState(task, null, titleOverride);
              }

              // If the local title override matches Firestore, it is stale and
              // should be removed so the task is not marked as modified forever.
              // This runs regardless of which side is newer so existing unedited
              // documents recover correctly.
              if (titleOverride) {
                const articleTitleMatches =
                  titleOverride.articleTitle === undefined || titleOverride.articleTitle === task.articleTitle;
                const presetNameMatches =
                  titleOverride.presetName === undefined || titleOverride.presetName === task.presetName;
                if (articleTitleMatches && presetNameMatches) {
                  await localDbService.deleteTitleOverride(task.id);
                }
              }

              return mergeLocalState(task, existingDraft, titleOverride);
            }));

            setTasks(reconciledTasks);
            tasksRef.current = reconciledTasks;
            setTotalTaskCount(reconciledTasks.length);
            setIsLoading(false);
          },
          (error) => {
            console.error("[App] Subscription error:", error);
            setError("데이터를 불러오는 중 권한 오류가 발생했습니다.");
            setIsLoading(false);
          },
          permission?.allowedAgents
        );
    } else {
      setIsLoading(false);
    }

    // Check for redirect after login
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      }
    });

    return () => {
      console.log("[App] Cleaning up auth listener and subscriptions");
      unsubscribe();
      if (unsubscribeNewTasksRef.current) unsubscribeNewTasksRef.current();
    };
  }, []);

  // Re-fetch count is no longer needed as we rely on local tasks length
  useEffect(() => {
    if (selectedAgentName === 'all') {
      setTotalTaskCount(tasks.length);
    } else {
      setTotalTaskCount(tasks.filter(t => t.agentName === selectedAgentName).length);
    }
  }, [selectedAgentName, tasks]);

  const handleDeleteTask = async (taskId: string) => {
    try {
      // 1. Delete from Firestore
      await deleteTask(taskId);
      
      // 2. Delete from LocalDB
      await localDbService.deleteDraft(taskId);
      await localDbService.deleteTitleOverride(taskId);
      
      // 3. Update UI state
      const updatedTasks = tasksRef.current.filter(t => t.id !== taskId);
      setTasks(updatedTasks);
      tasksRef.current = updatedTasks;
      
      // 4. Update total count
      setTotalTaskCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("[App] handleDeleteTask error:", err);
    }
  };

  const handleBulkDelete = async (taskIds: string[]) => {
    console.log(`[App] handleBulkDelete triggered for ${taskIds.length} tasks`);
    if (!taskIds || taskIds.length === 0) {
      console.warn("[App] No task IDs provided for bulk delete");
      return;
    }

    try {
      // Process in chunks to avoid overwhelming the browser/network/firestore
      const chunkSize = 15;
      const totalChunks = Math.ceil(taskIds.length / chunkSize);
      
      for (let i = 0; i < taskIds.length; i += chunkSize) {
        const chunk = taskIds.slice(i, i + chunkSize);
        const currentChunkIdx = Math.floor(i / chunkSize) + 1;
        console.log(`[App] Deleting chunk ${currentChunkIdx}/${totalChunks} (${chunk.length} items)`);
        
        // Use individual try-catches for each task to be resilient
        await Promise.all(chunk.map(async (id) => {
          try {
            await deleteTask(id);
            await localDbService.deleteDraft(id);
            await localDbService.deleteTitleOverride(id);
          } catch (taskErr) {
            console.error(`[App] Failed to delete task ${id}:`, taskErr);
            // Continue with other tasks
          }
        }));
        
        console.log(`[App] Chunk ${currentChunkIdx} processed`);
      }
      
      // Force update UI state immediately (Firestore onSnapshot will also sync later)
      console.log("[App] Updating local state after bulk deletion...");
      const updatedTasks = tasksRef.current.filter(t => !taskIds.includes(t.id));
      setTasks(updatedTasks);
      tasksRef.current = updatedTasks;
      setTotalTaskCount(updatedTasks.length);
      
      console.log(`[App] Bulk delete process finished. Sent ${taskIds.length} delete requests.`);
    } catch (err) {
      console.error("[App] handleBulkDelete major error:", err);
      alert(`일괄 삭제 중 중대 오류 발생: ${err instanceof Error ? err.message : String(err)}`);
      throw err; 
    }
  };

  const mergeLocalState = (
    task: GenerationTask,
    draft: LocalDraft | null,
    override: TitleOverride | null
  ): GenerationTask => {
    const result = { ...task };

    if (draft) {
      result.outputContent = draft.outputContent;
      if (draft.isWeekend !== undefined) result.isWeekend = draft.isWeekend;
      if (draft.category !== undefined) result.category = draft.category;
      if (draft.isRegistered !== undefined) result.isRegistered = draft.isRegistered;
    }

    // Only apply override fields that actually differ from the current Firestore
    // values. Stale overrides that match Firestore should not mark the task as
    // modified (orange title) forever.
    const effectiveOverride = override
      ? {
          ...override,
          articleTitle:
            override.articleTitle !== undefined && override.articleTitle !== task.articleTitle
              ? override.articleTitle
              : undefined,
          presetName:
            override.presetName !== undefined && override.presetName !== task.presetName
              ? override.presetName
              : undefined,
        }
      : null;

    if (effectiveOverride) {
      if (effectiveOverride.articleTitle !== undefined) result.articleTitle = effectiveOverride.articleTitle;
      if (effectiveOverride.presetName !== undefined) result.presetName = effectiveOverride.presetName;
    }

    // A cached draft that mirrors Firestore is NOT a local edit. Only count it
    // when the body actually differs from Firestore. Title edits are tracked
    // separately via effectiveOverride.
    const draftHasBodyEdit = draft ? draft.outputContent !== task.outputContent : false;
    const hasLocalEdits = !!(
      draftHasBodyEdit ||
      (effectiveOverride &&
        (effectiveOverride.articleTitle !== undefined || effectiveOverride.presetName !== undefined))
    );
    result.hasLocalEdits = hasLocalEdits;
    result.updatedAt = Math.max(
      task.updatedAt || 0,
      draft?.updatedAt || 0,
      effectiveOverride?.updatedAt || 0,
      result.createdAt + (hasLocalEdits ? 1 : 0)
    );

    return result;
  };

  const handleLogin = async () => {
    try {
      await signIn();
    } catch (e) {
      console.error("Login failed", e);
    }
  };

  // Editor State
  const [editorContent, setEditorContent] = useState<string>('');
  const [hasRestoredEditor, setHasRestoredEditor] = useState(false);

  // Restore editor content on mount when sessionStorage has editor state
  useEffect(() => {
    if (hasRestoredEditor) return;
    if (viewMode === ViewMode.EDITOR && activeTaskId && tasks.length > 0) {
      const loadDraft = async () => {
        const draft = await localDbService.getDraft(activeTaskId);
        if (draft) {
          setEditorContent(draft.outputContent);
        } else {
          const task = tasksRef.current.find(t => t.id === activeTaskId);
          if (task) setEditorContent(task.outputContent);
        }
        setHasRestoredEditor(true);
      };
      loadDraft();
    }
  }, [viewMode, activeTaskId, tasks, hasRestoredEditor]);

  const handleEditTask = async (taskId: string) => {
    setActiveTaskId(taskId);
    setViewMode(ViewMode.EDITOR);

    // 1. Check IndexedDB for a local draft and title override
    const [draft, titleOverride] = await Promise.all([
      localDbService.getDraft(taskId),
      localDbService.getTitleOverride(taskId)
    ]);

    // 2. Merge local state into the current task so local edits survive across sessions.
    const taskIndex = tasksRef.current.findIndex(t => t.id === taskId);
    const task = taskIndex !== -1 ? tasksRef.current[taskIndex] : null;

    if (task) {
      const mergedTask = mergeLocalState(task, draft, titleOverride);
      tasksRef.current[taskIndex] = mergedTask;
      setTasks([...tasksRef.current]);
      setEditorContent(draft ? draft.outputContent : mergedTask.outputContent);
    } else {
      setEditorContent(draft ? draft.outputContent : '');
    }
  };

  const handleSaveEditorContent = async (newContent: string, articleTitle: string) => {
    if (activeTaskId) {
      const task = tasksRef.current.find(t => t.id === activeTaskId);
      
      // CRITICAL: Check if content or title actually changed before updating updatedAt
      const isContentChanged = task?.outputContent !== newContent;
      const isTitleChanged = task?.articleTitle !== articleTitle;
      
      if (!isContentChanged && !isTitleChanged && task?.presetName !== '새 문서') {
        return; // No real change, don't update updatedAt or save
      }

      const newUpdatedAt = Date.now();

      // Determine the final preset name (sync default name with article title)
      let updatedPresetName = task?.presetName || '새 문서';
      // Keep the generator list title in sync with the edited article title
      // when the preset name is still the default or matches the old title.
      if (articleTitle && (updatedPresetName === '새 문서' || updatedPresetName === task?.articleTitle)) {
        updatedPresetName = articleTitle;
      }

      // 1. Save to IndexedDB immediately (Real-time local persistence)
      await localDbService.saveDraft(activeTaskId, newContent, articleTitle, updatedPresetName, {
        isWeekend: task?.isWeekend,
        category: task?.category,
        isRegistered: task?.isRegistered,
        agentName: task?.agentName,
        createdAt: task?.createdAt,
        updatedAt: newUpdatedAt
      });

      // 2. Persist title override separately so it survives local draft storage
      // limits and stays available across sessions.
      // Only persist when the user explicitly edited the article title. Do NOT
      // persist the automatic presetName-to-articleTitle sync here; that sync is a
      // side-effect of editing, not a user title edit, and storing it makes
      // unedited tasks appear orange forever.
      if (isTitleChanged && articleTitle.trim() !== '') {
        await localDbService.saveTitleOverride(activeTaskId, {
          articleTitle,
          updatedAt: newUpdatedAt
        });
      }

      // 3. Update editorContent state immediately for UI responsiveness
      setEditorContent(newContent);

      // 4. Update the tasks array synchronously via ref
      const taskIndex = tasksRef.current.findIndex(t => t.id === activeTaskId);
      if (taskIndex !== -1) {
        const currentTask = tasksRef.current[taskIndex];

        const updatedTask = {
          ...currentTask,
          outputContent: newContent,
          articleTitle,
          presetName: updatedPresetName,
          updatedAt: newUpdatedAt,
          hasLocalEdits: true
        };

        // Update ref synchronously
        tasksRef.current[taskIndex] = updatedTask;

        // Update state for UI
        setTasks([...tasksRef.current]);

        // CRITICAL: DO NOT Trigger Firestore save during editing as per user request.
        // Only save to LocalDB (already done above)
      }
    }
  };

  const handleNewTask = async (newTask: GenerationTask) => {
    // 1. Update ref and state synchronously
    const updatedTasks = [newTask, ...tasksRef.current];
    tasksRef.current = updatedTasks;
    setTasks([...updatedTasks]);
    setTotalTaskCount(prev => prev + 1);
    
    // 2. Save to Server (which handles Firestore + Notion)
    try {
      const response = await fetch(`${API_BASE}/api/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': import.meta.env.VITE_API_KEY || ''
        },
        body: JSON.stringify(newTask)
      });
      
      if (!response.ok) {
        throw new Error(`Server save failed: ${response.statusText}`);
      }
      
      const result = await response.json();
      if (result.notionPageId) {
        // Update local state with notionPageId
        const taskIndex = tasksRef.current.findIndex(t => t.id === newTask.id);
        if (taskIndex !== -1) {
          const updatedWithNotion = { ...tasksRef.current[taskIndex], notionPageId: result.notionPageId };
          tasksRef.current[taskIndex] = updatedWithNotion;
          setTasks([...tasksRef.current]);
          
          // Also update LocalDB
          await localDbService.saveDraft(newTask.id, updatedWithNotion.outputContent, updatedWithNotion.articleTitle || '', newTask.presetName, {
            isWeekend: updatedWithNotion.isWeekend,
            category: updatedWithNotion.category,
            isRegistered: updatedWithNotion.isRegistered,
            agentName: updatedWithNotion.agentName,
            createdAt: updatedWithNotion.createdAt,
            notionPageId: result.notionPageId
          } as any);
        }
      }
    } catch (e) {
      console.error("[App] handleNewTask server save failed", e);
      // Fallback to direct Firestore save
      await saveTask(newTask);
    }
  };

  const handleToggleRegister = async (isRegistered: boolean) => {
    if (activeTaskId) {
      const taskToUpdate = tasksRef.current.find(t => t.id === activeTaskId);
      if (taskToUpdate) {
        // 1. Determine the correct agentName
        const finalAgentName = taskToUpdate.agentName || '기본 에이전트';

        // 2. Prepare the updated task object
        const category = taskToUpdate.category || getDefaultCategory(taskToUpdate.presetName);
        const daumPublishedAt = isRegistered ? null : (taskToUpdate.daumPublishedAt || null);

        let fullHtml = '';
        if (isRegistered) {
          // CRITICAL: Always fetch the latest content from IndexedDB first.
          // tasksRef may hold stale data if the editor was reset by an external
          // initialContent change before the latest edits were synced.
          let latestOutputContent = taskToUpdate.outputContent || '';
          try {
            const draft = await localDbService.getDraft(activeTaskId);
            if (draft?.outputContent) {
              latestOutputContent = draft.outputContent;
            }
          } catch (e) {
            console.warn('[App] Failed to fetch latest draft from IndexedDB, falling back to task outputContent', e);
          }

          // Convert markdown to HTML for fullHtml field
          let bodyMarkdown = latestOutputContent;
          const separatorRegex = /(?:^|\n)~~~(?:\n|$)/;
          const separatorMatch = bodyMarkdown.match(separatorRegex);
          if (separatorMatch && separatorMatch.index !== undefined) {
            bodyMarkdown = bodyMarkdown.substring(separatorMatch.index + separatorMatch[0].length).trim();
          }
          // Strip trailing --- delimiter
          const endDashRegex = /(?:^|\n)---\s*$/;
          bodyMarkdown = bodyMarkdown.replace(endDashRegex, '').trim();

          const bodyHtml = await marked.parse(prepareMarkdownForRender(bodyMarkdown));
          const title = taskToUpdate.articleTitle || 'Generated Document';
          const titleLines = title.split('\n').filter(l => l.trim());
          
          fullHtml = generateFullHtml(taskToUpdate, bodyHtml, titleLines[0]);
        }

        const updatedTask = { 
          ...taskToUpdate, 
          isRegistered, 
          category, 
          agentName: finalAgentName, 
          daumPublishedAt,
          fullHtml,
          updatedAt: Date.now()
        };
        
        // 3. Update ref and state synchronously
        const taskIndex = tasksRef.current.findIndex(t => t.id === activeTaskId);
        if (taskIndex !== -1) {
          tasksRef.current[taskIndex] = updatedTask;
          setTasks([...tasksRef.current]);
        }
        
        // 4. Save to IndexedDB for local persistence
        await localDbService.saveDraft(activeTaskId, updatedTask.outputContent, updatedTask.articleTitle || '', updatedTask.presetName, {
          isWeekend: updatedTask.isWeekend,
          category: updatedTask.category,
          isRegistered: isRegistered,
          agentName: updatedTask.agentName,
          createdAt: updatedTask.createdAt,
          updatedAt: updatedTask.updatedAt
        });
        
        // 5. Save to Firestore & Trigger Server-side Sync (Pub/Sub)
        try {
          const apiKey = (import.meta as any).env?.VITE_API_KEY || 'development-key';
          const response = await fetch(`${API_BASE}/api/tasks/${activeTaskId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
            body: JSON.stringify({
              isRegistered,
              isWeekend: updatedTask.isWeekend || false,
              category,
              agentName: finalAgentName,
              articleTitle: updatedTask.articleTitle || updatedTask.presetName || '',
              outputContent: updatedTask.outputContent,
              fullHtml: updatedTask.fullHtml,
              isContentInStorage: updatedTask.isContentInStorage,
              daumPublishedAt: updatedTask.daumPublishedAt
            }),
          });

          if (!response.ok) {
            throw new Error(`Server sync failed: ${response.statusText}`);
          }

          const result = await response.json();
          if (result.notionPageId) {
            const taskIndex = tasksRef.current.findIndex(t => t.id === activeTaskId);
            if (taskIndex !== -1) {
              const updatedWithNotion = { ...tasksRef.current[taskIndex], notionPageId: result.notionPageId };
              tasksRef.current[taskIndex] = updatedWithNotion;
              setTasks([...tasksRef.current]);

              await localDbService.saveDraft(activeTaskId, updatedWithNotion.outputContent, updatedWithNotion.articleTitle || '', updatedTask.presetName, {
                isWeekend: updatedWithNotion.isWeekend,
                category: updatedWithNotion.category,
                isRegistered: updatedWithNotion.isRegistered,
                agentName: updatedWithNotion.agentName,
                createdAt: updatedWithNotion.createdAt,
                updatedAt: updatedWithNotion.updatedAt,
                notionPageId: result.notionPageId
              } as any);
            }
          }

          console.log(`[App] Task ${activeTaskId} server-side sync completed. isRegistered: ${isRegistered}`);
        } catch (e) {
          console.error("[App] Server sync failed", e);
          await saveTask(updatedTask);
        }
      }
    }
  };

  const handleToggleWeekend = async (isWeekend: boolean) => {
    if (activeTaskId) {
      const taskToUpdate = tasksRef.current.find(t => t.id === activeTaskId);
      if (taskToUpdate) {
        const newUpdatedAt = Date.now();
        const updatedTask = { ...taskToUpdate, isWeekend, updatedAt: newUpdatedAt };
        
        // Update ref and state
        const taskIndex = tasksRef.current.findIndex(t => t.id === activeTaskId);
        if (taskIndex !== -1) {
          tasksRef.current[taskIndex] = updatedTask;
          setTasks([...tasksRef.current]);
        }
        
        // Save to IndexedDB for persistence
        await localDbService.saveDraft(activeTaskId, updatedTask.outputContent, updatedTask.articleTitle || '', updatedTask.presetName, {
          isWeekend,
          category: updatedTask.category,
          isRegistered: updatedTask.isRegistered,
          agentName: updatedTask.agentName,
          createdAt: updatedTask.createdAt,
          updatedAt: newUpdatedAt,
          notionPageId: updatedTask.notionPageId
        });

        // CRITICAL: DO NOT Trigger Firestore save during editing as per user request.
      }
    }
  };

  const handleToggleCategory = async (category: string) => {
    if (activeTaskId) {
      const taskToUpdate = tasksRef.current.find(t => t.id === activeTaskId);
      if (taskToUpdate) {
        const newUpdatedAt = Date.now();
        const updatedTask = { ...taskToUpdate, category, updatedAt: newUpdatedAt };
        
        // Update ref and state
        const taskIndex = tasksRef.current.findIndex(t => t.id === activeTaskId);
        if (taskIndex !== -1) {
          tasksRef.current[taskIndex] = updatedTask;
          setTasks([...tasksRef.current]);
        }
        
        // Save to IndexedDB for persistence
        await localDbService.saveDraft(activeTaskId, updatedTask.outputContent, updatedTask.articleTitle || '', updatedTask.presetName, {
          isWeekend: updatedTask.isWeekend,
          category,
          isRegistered: updatedTask.isRegistered,
          agentName: updatedTask.agentName,
          createdAt: updatedTask.createdAt,
          updatedAt: newUpdatedAt,
          notionPageId: updatedTask.notionPageId
        });

        // CRITICAL: DO NOT Trigger Firestore save during editing as per user request.
      }
    }
  };

  const handleRenameTask = async (taskId: string, newName: string) => {
    const taskToUpdate = tasksRef.current.find(t => t.id === taskId);
    if (taskToUpdate && taskToUpdate.presetName !== newName) {
      const newUpdatedAt = Date.now();
      const updatedTask = { ...taskToUpdate, presetName: newName, updatedAt: newUpdatedAt, hasLocalEdits: true };
      
      // 1. Update ref and state
      const taskIndex = tasksRef.current.findIndex(t => t.id === taskId);
      if (taskIndex !== -1) {
        tasksRef.current[taskIndex] = updatedTask;
        setTasks([...tasksRef.current]);
      }
      
      // 2. Save to IndexedDB
      await localDbService.saveDraft(taskId, updatedTask.outputContent, updatedTask.articleTitle || '', updatedTask.presetName, {
        isWeekend: updatedTask.isWeekend,
        category: updatedTask.category,
        isRegistered: updatedTask.isRegistered,
        agentName: updatedTask.agentName,
        createdAt: updatedTask.createdAt,
        updatedAt: newUpdatedAt,
        notionPageId: updatedTask.notionPageId
      });

      // 3. Persist title override separately so it survives local draft storage
      // limits and stays available across sessions.
      await localDbService.saveTitleOverride(taskId, {
        presetName: newName,
        updatedAt: newUpdatedAt
      });

      // 4. CRITICAL: DO NOT Trigger Firestore save during editing as per user request.
    }
  };

  const handleImagesChange = async (taskId: string, images: string[]) => {
    const taskToUpdate = tasksRef.current.find(t => t.id === taskId);
    if (taskToUpdate) {
      const newUpdatedAt = Date.now();
      const updatedTask = { ...taskToUpdate, images, updatedAt: newUpdatedAt };
      
      // Update ref and state
      const taskIndex = tasksRef.current.findIndex(t => t.id === taskId);
      if (taskIndex !== -1) {
        tasksRef.current[taskIndex] = updatedTask;
        setTasks([...tasksRef.current]);
      }
      
      // 2. Save to IndexedDB for local persistence
      await localDbService.saveDraft(taskId, updatedTask.outputContent, updatedTask.articleTitle || '', updatedTask.presetName, {
        isWeekend: updatedTask.isWeekend,
        category: updatedTask.category,
        isRegistered: updatedTask.isRegistered,
        agentName: updatedTask.agentName,
        createdAt: updatedTask.createdAt,
        updatedAt: newUpdatedAt,
        notionPageId: updatedTask.notionPageId
      });

      // 3. CRITICAL: DO NOT Trigger Firestore save during editing as per user request.
    }
  };

  if (isLoading || (user && !userPermission)) {
    return (
      <div className="h-screen w-screen bg-slate-900 flex flex-col items-center justify-center text-white">
        <div className="text-xl font-bold animate-pulse mb-4">보안 환경 설정 중...</div>
        <div className="text-slate-400 text-xs">잠시만 기다려 주세요. 권한을 확인하고 있습니다.</div>
        {error && (
          <div className="max-w-md text-center px-6 mt-8">
            <p className="text-red-400 text-sm mb-4">{error}</p>
            <div className="flex flex-col gap-2">
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs transition-colors"
              >
                새로고침
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const handleBackToGenerator = () => {
    setViewMode(ViewMode.GENERATOR);
    setActiveTaskId(null);
  };

  if (!user) {
    return (
      <div className="h-screen w-screen bg-slate-900 flex flex-col items-center justify-center text-white p-6">
        <h1 className="text-4xl font-bold mb-8 tracking-tight">편집기</h1>
        <p className="text-slate-400 mb-8 text-center max-w-md">
          문서를 안전하게 서버에 저장하고 어디서든 확인하려면 로그인이 필요합니다.
        </p>
        <button 
          onClick={handleLogin}
          className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-semibold transition-all shadow-lg shadow-blue-900/20 flex items-center gap-3"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          Google 계정으로 시작하기
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-slate-50 overflow-hidden flex flex-col">
      {error && (
        <div className="fixed top-0 left-0 right-0 z-[300] bg-red-600 text-white px-6 py-3 flex items-center justify-between shadow-2xl">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex items-center gap-3">
              <span className="font-bold text-sm">서버 연결 오류 (할당량 초과 등)</span>
              <span className="text-xs opacity-90">{error}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setError(null)} className="p-2 hover:bg-white/10 rounded-full">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      <PermissionStatusBar permission={userPermission} user={user} totalCount={totalTaskCount} appVersion={APP_VERSION} buildTime={BUILD_TIME} />
      {/* Header with User Info */}
      <div className="bg-white border-b border-slate-200 px-3 md:px-6 py-2 flex flex-wrap justify-between items-center gap-2 text-[10px] text-slate-400">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-1.5 hover:bg-slate-100 rounded-md text-slate-600 transition-colors flex items-center gap-2"
            title="사이드바 토글"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="font-bold uppercase tracking-wider">사이드바 {isSidebarOpen ? '숨기기' : '보이기'}</span>
          </button>
          <div className="h-4 w-px bg-slate-200"></div>
          <div className="hidden sm:flex items-center gap-2">
            <span className="font-bold text-slate-500 uppercase tracking-wider">User UID:</span>
            <span className="font-mono">{user.uid}</span>
          </div>
          {isSyncing && (
            <div className="flex items-center gap-2 text-blue-500 animate-pulse">
              <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="font-bold">클라우드 동기화 중...</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-bold text-slate-500 uppercase tracking-wider">Email:</span>
          <span>{user.email}</span>
        </div>
      </div>
      
      <div className="flex-1 overflow-hidden relative">
        <div className={`h-full w-full ${viewMode === ViewMode.GENERATOR ? 'block' : 'hidden'}`}>
          <Generator 
            tasks={visibleTasks}
            agents={agents}
            onEditTask={handleEditTask} 
            onNewTask={handleNewTask}
            selectedAgentName={selectedAgentName}
            onSelectAgent={setSelectedAgentName}
            onDeleteTask={handleDeleteTask}
            onBulkDelete={handleBulkDelete}
            error={error}
            isSidebarOpen={isSidebarOpen}
            totalCount={totalTaskCount}
            categories={categories}
            onAddCategory={handleAddCategory}
            onRemoveCategory={handleRemoveCategory}
            newCategoryName={newCategoryName}
            setNewCategoryName={setNewCategoryName}
            showAddCategory={showAddCategory}
            setShowAddCategory={setShowAddCategory}
            isAdmin={userPermission?.isAdmin}
            onOpenChannelEditor={() => setViewMode(ViewMode.PERMISSIONS)}
          />
        </div>
        <div className={`h-full w-full overflow-y-auto ${viewMode === ViewMode.PERMISSIONS ? 'block' : 'hidden'}`}>
          <ChannelManagement 
            onBack={handleBackToGenerator}
            availableAgents={allAvailableAgents}
            tasks={tasks}
            onBulkDelete={handleBulkDelete}
          />
        </div>
        <div className={`h-full w-full ${viewMode === ViewMode.EDITOR ? 'block' : 'hidden'}`}>
          <Editor 
            initialContent={editorContent} 
            articleTitle={visibleTasks.find(t => t.id === activeTaskId)?.articleTitle}
            onSave={handleSaveEditorContent}
            onImagesChange={handleImagesChange}
            onBack={handleBackToGenerator} 
            tasks={visibleTasks}
            agents={agents}
            activeTaskId={activeTaskId || ''}
            onSwitchTask={handleEditTask}
            isRegistered={visibleTasks.find(t => t.id === activeTaskId)?.isRegistered}
            isWeekend={visibleTasks.find(t => t.id === activeTaskId)?.isWeekend}
            category={visibleTasks.find(t => t.id === activeTaskId)?.category}
            onToggleRegister={handleToggleRegister}
            onToggleWeekend={handleToggleWeekend}
            onToggleCategory={handleToggleCategory}
            onRenameTask={handleRenameTask}
            selectedAgentName={selectedAgentName}
            onSelectAgent={setSelectedAgentName}
            isSidebarOpen={isSidebarOpen}
            categories={categories}
            onAddCategory={handleAddCategory}
            onRemoveCategory={handleRemoveCategory}
            newCategoryName={newCategoryName}
            setNewCategoryName={setNewCategoryName}
            showAddCategory={showAddCategory}
            setShowAddCategory={setShowAddCategory}
          />
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="*" element={<AppContent />} />
      </Routes>
    </Router>
  );
}

export default App;